import { memo } from 'react';
import { Badge } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  STATUS_BADGE_COLORS,
  STATUS_BADGE_I18N_PREFIXES,
} from '@/constants/statusColors';
import type { StatusBadgeProps, StatusBadgeType } from './StatusBadge.types';

/**
 * Get the color for a given badge type and value
 * Falls back to 'gray' for unknown values
 */
function getColor(type: StatusBadgeType, value: string): string {
  const colorMap = STATUS_BADGE_COLORS[type] as Record<string, string>;
  return colorMap[value] ?? 'gray';
}

/**
 * Get the i18n translation key for a given badge type and value
 * Handles case conversion for role and invitationStatus types
 * (API returns PascalCase but i18n keys are lowercase)
 */
function getTranslationKey(type: StatusBadgeType, value: string): string {
  const prefix = STATUS_BADGE_I18N_PREFIXES[type];

  // Role and invitationStatus use lowercase i18n keys
  // but may receive PascalCase values from the API
  if (type === 'role' || type === 'invitationStatus') {
    return `${prefix}.${value.toLowerCase()}`;
  }

  // taskStatus and priority already use lowercase values
  return `${prefix}.${value}`;
}

/**
 * StatusBadge Component
 *
 * Unified badge for displaying task status, priority, roles, and invitation status
 * with consistent colors and i18n support.
 *
 * @example
 * ```tsx
 * // Task status badge
 * <StatusBadge type="taskStatus" value="in_progress" />
 *
 * // Priority badge
 * <StatusBadge type="priority" value="high" variant="filled" />
 *
 * // Role badge
 * <StatusBadge type="role" value="Admin" size="md" />
 *
 * // Invitation status badge
 * <StatusBadge type="invitationStatus" value="Pending" />
 * ```
 */
export const StatusBadge = memo(function StatusBadge({
  type,
  value,
  size = 'sm',
  variant = 'light',
}: StatusBadgeProps) {
  const { t } = useTranslation();

  // Get color from mapping, fallback to gray for unknown values
  const color = getColor(type, value);

  // Get translated label
  const translationKey = getTranslationKey(type, value);
  const label = t(translationKey);

  return (
    <Badge color={color} variant={variant} size={size}>
      {label}
    </Badge>
  );
});

StatusBadge.displayName = 'StatusBadge';
